import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, onSnapshot, getDocs, updateDoc, serverTimestamp, orderBy, limit, writeBatch } from 'firebase/firestore';
import Login from './Login';
import ScoreEntry from './ScoreEntry';
import EditScoreEntry from './EditScoreEntry';
import AdminPanel from './AdminPanel';
import MarkerDashboard from './MarkerDashboard';
import MyRounds from './MyRounds';
import Leaderboard from './Leaderboard';
import FunStats from './FunStats';
import './App.css';
import logo from './assets/logo.png';
import MyProfile from './MyProfile';
import Players from './Players';
import './App.css';
import { getHolesByCourse } from './courseData';
import { computeFinaleLeaderboard, formatFinaleScore, formatTodayScore, getScoreColor } from './finaleUtils';
import { trackPageView, updateUserPresence, startSession, endSession, updateSessionActivity } from './analytics';
import SiteAnalytics from './SiteAnalytics';
import NotificationBell from './NotificationBell';
import SeasonRecap from './SeasonRecap';
import Settings from './Settings';
import FinaleWinnerPopup from './FinaleWinnerPopup';
import { createNotification } from './notifications';
// KWAC Brand Colors
// Professional Dark Theme Colors
const colors = {
  bgDark: '#0f0f0f',
  bgCard: '#1e1e1e',
  bgCardHover: '#252525',
  borderSubtle: '#2d2d2d',
  gold: '#d4af37',
  goldDark: '#b8941f',
  textPrimary: '#ffffff',
  textSecondary: '#9ca3af',
  textTertiary: '#6b7280',
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
  
  // Legacy
  primary: '#d4af37',
  primaryDark: '#b8941f',
  black: '#0f0f0f',
  darkGray: '#1a1a1a',
  white: '#ffffff',
  lightGray: '#f7fafc',
  textGray: '#9ca3af',
  textLight: '#6b7280'
};

// "Today" as YYYY-MM-DD in the browser's local timezone. new Date().toISOString()
// converts to UTC first, which rolls over to the next/previous calendar day near
// midnight in timezones ahead of UTC (e.g. CEST) — that mismatch made finale_18
// events dated "today" fail their date match until UTC caught up.
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Compute leaderboard positions from a set of rounds (with drop system matching the main leaderboard)
const computePositionsFromRounds = (rounds) => {
  const userStats = {};
  rounds.forEach(round => {
    const userId = round.userId;
    if (!userStats[userId]) {
      userStats[userId] = { userId, totalPoints: 0, roundsPlayed: 0, allScores: [] };
    }
    userStats[userId].totalPoints += round.totalStableford || 0;
    userStats[userId].roundsPlayed += 1;
    userStats[userId].allScores.push(round.totalStableford || 0);
  });
  const lb = Object.values(userStats).map(stats => {
    let dropCount = 0;
    if (stats.roundsPlayed >= 25) dropCount = 4;
    else if (stats.roundsPlayed >= 20) dropCount = 3;
    else if (stats.roundsPlayed >= 15) dropCount = 2;
    else if (stats.roundsPlayed >= 10) dropCount = 1;
    let adjustedTotal = stats.totalPoints;
    let countingRounds = stats.roundsPlayed;
    if (dropCount > 0) {
      const sorted = [...stats.allScores].sort((a, b) => a - b);
      adjustedTotal -= sorted.slice(0, dropCount).reduce((s, v) => s + v, 0);
      countingRounds -= dropCount;
    }
    return {
      userId: stats.userId,
      averagePoints: parseFloat(countingRounds > 0 ? (adjustedTotal / countingRounds).toFixed(2) : '0.00')
    };
  });
  lb.sort((a, b) => b.averagePoints - a.averagePoints);
  const positions = {};
  lb.forEach((p, i) => { positions[p.userId] = i + 1; });
  return positions;
};

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScoreEntry, setShowScoreEntry] = useState(false);
  const [showEditScoreEntry, setShowEditScoreEntry] = useState(false);
  const [editRoundData, setEditRoundData] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showMarkerDashboard, setShowMarkerDashboard] = useState(false);
  const [showMyRounds, setShowMyRounds] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showFunStats, setShowFunStats] = useState(false);
  const [pendingMembersCount, setPendingMembersCount] = useState(0);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [disputedRoundsCount, setDisputedRoundsCount] = useState(0);
  const [savedRound, setSavedRound] = useState(null);
  const [showRoundBanner, setShowRoundBanner] = useState(false);
  const [pendingBeer, setPendingBeer] = useState(null);
  const [leaderboardExpanded, setLeaderboardExpanded] = useState(false);
  const [showMyProfile, setShowMyProfile] = useState(false);
  const [viewingProfileId, setViewingProfileId] = useState(null);
  const [hallOfFame, setHallOfFame] = useState([]);
  const [showPlayers, setShowPlayers] = useState(false);
  const [hallOfFameExpanded, setHallOfFameExpanded] = useState(false);
  const [seasonRecords, setSeasonRecords] = useState([]);
  const [recordsExpanded, setRecordsExpanded] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showSiteAnalytics, setShowSiteAnalytics] = useState(false);
  const [showSeasonRecap, setShowSeasonRecap] = useState(false);
  const [recapAvailable, setRecapAvailable] = useState(false);
  const [activeRecapSeasonId, setActiveRecapSeasonId] = useState(null);
  const [showRecapBanner, setShowRecapBanner] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFinaleWinner, setShowFinaleWinner] = useState(false);
  const [finaleWinnerData, setFinaleWinnerData] = useState(null);
  const [todayFinaleEvent, setTodayFinaleEvent] = useState(null);
  const [finaleLeaderboard, setFinaleLeaderboard] = useState([]);
  const [finaleLeaderboardExpanded, setFinaleLeaderboardExpanded] = useState(false);
  const [finalePlayerData, setFinalePlayerData] = useState([]);
  const [seasonLeaderboardCollapsed, setSeasonLeaderboardCollapsed] = useState(false);
  const [settingsTab, setSettingsTab] = useState('editProfile');
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [dropdownHover, setDropdownHover] = useState(null);
  const profileDropdownRef = useRef(null);

  // Hide-on-scroll header
  const [headerVisible, setHeaderVisible] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);
  const headerResizeObserverRef = useRef(null);
  const lastScrollYRef = useRef(0);

  // Callback ref (not a plain useRef effect) because the header doesn't exist
  // in the DOM yet during the loading/pending/denied early returns — an effect
  // with [] deps would measure a null node on first mount and never fire again
  // once the real header appears, leaving the spacer permanently collapsed.
  const setHeaderRef = useCallback((node) => {
    headerResizeObserverRef.current?.disconnect();
    headerRef.current = node;
    if (node) {
      setHeaderHeight(node.offsetHeight);
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => setHeaderHeight(node.offsetHeight));
        observer.observe(node);
        headerResizeObserverRef.current = observer;
      }
    }
  }, []);

  // Leaderboard data
  const [activeSeason, setActiveSeason] = useState(null);
  const [seasonLeaderboard, setSeasonLeaderboard] = useState([]);
  const [users, setUsers] = useState({});
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [dashboardPositionChanges, setDashboardPositionChanges] = useState({});

  // Toast notification system
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [toastQueue, setToastQueue] = useState([]);
  const [pendingToasts, setPendingToasts] = useState([]);
  const pendingToastsRef = useRef([]);
  const bundleTimerRef = useRef(null);
  const [currentToast, setCurrentToast] = useState(null);
  const [toastDismissing, setToastDismissing] = useState(false);
  const toastTimerRef = useRef(null);
  const toastCardRef = useRef(null);
  // Only toast notifications created since this device last saw one. Persisting the
  // watermark means a refresh doesn't replay toasts the user has already been shown.
  const getLastSeenTime = () => {
    const stored = localStorage.getItem('finaleNotifLastSeen');
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : Date.now();
  };
  const appLoadTimeRef = useRef(getLastSeenTime());
  const toastedNotifIdsRef = useRef(new Set());
  const prevFinaleDataRef = useRef({});
  const recentNotifRef = useRef({});
  const finalePrevLeaderRef = useRef(null);
  const finalePrevCloseRef = useRef({});
  const holeStreakFiredRef = useRef({});
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);

  // Notification shade (mobile pull-down notification center)
  const [shadeOpen, setShadeOpen] = useState(false);
  const [shadeNotifications, setShadeNotifications] = useState([]);
  const [shadeDismissing, setShadeDismissing] = useState(false);
  const [unreadShadeCount, setUnreadShadeCount] = useState(0);
  const [shadeDeleteConfirm, setShadeDeleteConfirm] = useState(false);
  const [shadeLoadError, setShadeLoadError] = useState(false);
  const shadeOpenRef = useRef(false);



  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          } else {
            console.warn('User profile not found');
            setUserProfile(null);
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const checkSavedRound = () => {
      const saved = localStorage.getItem('golfRoundInProgress');
      if (saved) {
        const data = JSON.parse(saved);
        // Ignore progress saved under a different account (e.g. switching users
        // on the same device) — otherwise this banner would offer to resume
        // someone else's in-progress round.
        if (data.userId && data.userId !== user?.uid) {
          setShowRoundBanner(false);
        } else {
          const savedTime = new Date(data.timestamp);
          const now = new Date();
          const hoursDiff = (now - savedTime) / (1000 * 60 * 60);

          if (hoursDiff < 24) {
            setSavedRound(data);
            setShowRoundBanner(true);
          } else {
            localStorage.removeItem('golfRoundInProgress');
          }
        }
      }

      const beerEntry = localStorage.getItem('pendingBeerEntry');
      if (beerEntry) {
        setPendingBeer(JSON.parse(beerEntry));
      }
    };

    checkSavedRound();
    const interval = setInterval(checkSavedRound, 5000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!userProfile) return;
    
    const canApprovemembers = ['admin', 'moderator', 'officer'].includes(userProfile.role);
    if (!canApprovemembers) return;

    const q = query(collection(db, 'users'), where('status', '==', 'pending'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingMembersCount(snapshot.size);
    });

    return unsubscribe;
  }, [userProfile]);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'rounds'),
      where('markerId', '==', user.uid),
      where('status', '==', 'pending')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingApprovalsCount(snapshot.size);
    });

    return unsubscribe;
  }, [user]);

  useEffect(() => {
  if (!user) return;

  const q = query(
    collection(db, 'rounds'),
    where('userId', '==', user.uid),
    where('status', '==', 'disputed')
  );
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    setDisputedRoundsCount(snapshot.size);
  });

  return unsubscribe;
}, [user]);

  // Load all users
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const usersMap = {};
      snapshot.docs.forEach(doc => {
        usersMap[doc.id] = doc.data();
      });
      setUsers(usersMap);
    });

    return unsubscribe;
  }, []);

  // Load active season
  useEffect(() => {
    const loadActiveSeason = async () => {
      try {
        const seasonsQuery = query(collection(db, 'seasons'), where('isActive', '==', true));
        const seasonsSnapshot = await getDocs(seasonsQuery);
        
        if (!seasonsSnapshot.empty) {
          const season = { id: seasonsSnapshot.docs[0].id, ...seasonsSnapshot.docs[0].data() };
          setActiveSeason(season);
        }
      } catch (error) {
        console.error('Error loading active season:', error);
      }
    };

    loadActiveSeason();
  }, []);

  // Check for published Season Recap
  useEffect(() => {
    if (!activeSeason) return;
    const checkRecap = async () => {
      try {
        const snap = await getDoc(doc(db, 'season_recaps', activeSeason.id));
        if (snap.exists() && snap.data().isPublished) {
          setRecapAvailable(true);
          setActiveRecapSeasonId(activeSeason.id);
          const seenKey = `kwac_recap_seen_${activeSeason.id}`;
          if (!localStorage.getItem(seenKey)) {
            setShowRecapBanner(true);
            setTimeout(() => setBannerVisible(true), 80);
          }
        } else {
          setRecapAvailable(false);
          setActiveRecapSeasonId(null);
        }
      } catch (err) {
        console.error('Error checking season recap:', err);
      }
    };
    checkRecap();
  }, [activeSeason]);

  // Check for revealed Season Finale winner — shown once per event, on the day of the finale
  useEffect(() => {
    if (!user || !activeSeason) return;
    const checkFinaleWinner = async () => {
      try {
        const today = getLocalDateString();
        const eventsQuery = query(
          collection(db, 'events'),
          where('seasonId', '==', activeSeason.id),
          where('type', '==', 'finale_18'),
          where('date', '==', today)
        );
        const snapshot = await getDocs(eventsQuery);
        for (const eventDoc of snapshot.docs) {
          const event = { id: eventDoc.id, ...eventDoc.data() };
          const seenKey = `finaleWinnerDismissed_${event.id}`;
          if (event.winnerRevealed === true && !localStorage.getItem(seenKey)) {
            setFinaleWinnerData({
              eventName: event.name,
              eventId: event.id,
              winnerId: event.winnerId,
              winnerName: event.winnerName,
              winnerNetGross: event.winnerNetGross,
              winnerPhotoURL: event.winnerPhotoURL,
            });
            setShowFinaleWinner(true);
            break;
          }
        }
      } catch (err) {
        console.error('Error checking finale winner:', err);
      }
    };
    checkFinaleWinner();
  }, [user, activeSeason]);

  // Detect whether the active season has a Season Finale event at all — the
  // finale transformation activates as soon as a finale_18 event exists, not
  // only on its date.
  // Live listener (not a one-time getDocs) so a finale_18 event created after
  // this device's app was already open still activates the pipeline without
  // requiring a page reload.
  useEffect(() => {
    if (!activeSeason) {
      setTodayFinaleEvent(null);
      setSeasonLeaderboardCollapsed(false);
      return;
    }
    const eventsQuery = query(
      collection(db, 'events'),
      where('seasonId', '==', activeSeason.id),
      where('type', '==', 'finale_18')
    );
    const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
      if (!snapshot.empty) {
        const eventDoc = snapshot.docs[0];
        setTodayFinaleEvent({ id: eventDoc.id, ...eventDoc.data() });
        setSeasonLeaderboardCollapsed(true);
      } else {
        setTodayFinaleEvent(null);
        setSeasonLeaderboardCollapsed(false);
      }
    }, (err) => {
      console.error('Error checking today finale event:', err);
      setTodayFinaleEvent(null);
      setSeasonLeaderboardCollapsed(false);
    });
    return () => unsubscribe();
  }, [activeSeason]);

  // Real-time listener on finaleRounds for the live Finale Day leaderboard
  useEffect(() => {
    prevFinaleDataRef.current = {};
    finalePrevLeaderRef.current = null;
    finalePrevCloseRef.current = {};
    holeStreakFiredRef.current = {};
    if (!todayFinaleEvent) {
      setFinaleLeaderboard([]);
      return;
    }
    const q = query(
      collection(db, 'finaleRounds'),
      where('eventId', '==', todayFinaleEvent.id)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rounds = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinaleLeaderboard(rounds);
    });
    return () => unsubscribe();
  }, [todayFinaleEvent]);

  // Compute the tournament-style leaderboard for every approved season player
  // (not just those who've started a finale round). Bonus strokes come from
  // finaleConfig.bonusAssignments, which is locked in at event-creation time —
  // recomputing season positions live here would drift from the bonus each
  // player's finaleRound was actually scored with in ScoreEntry.js.
  useEffect(() => {
    if (!todayFinaleEvent) {
      setFinalePlayerData([]);
      return;
    }
    let cancelled = false;

    const computeFinalePlayerData = async () => {
      try {
        const computed = await computeFinaleLeaderboard({
          todayFinaleEvent,
          activeSeason,
          finaleRounds: finaleLeaderboard,
          users,
          getUserName,
        });
        if (!cancelled) setFinalePlayerData(computed);
      } catch (err) {
        console.error('Error computing finale leaderboard:', err);
        if (!cancelled) setFinalePlayerData([]);
      }
    };

    computeFinalePlayerData();
    return () => { cancelled = true; };
  }, [finaleLeaderboard, todayFinaleEvent, users, activeSeason]);

  // Track viewport width to gate toast/shade UI to mobile only
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    shadeOpenRef.current = shadeOpen;
  }, [shadeOpen]);

  // Create a finale notification document. Every connected client independently
  // detects the same leaderboard milestones (this runs client-side, not server-side),
  // so a random addDoc() ID here would let each open browser write its own duplicate
  // document for the identical real-world event. Writing to a deterministic ID keyed
  // on the event itself instead means simultaneous detections from different clients
  // collapse onto the same document — the in-memory recentNotifRef check only guards
  // against a single client re-firing, not against other clients.
  const createFinaleNotification = async (data) => {
    if (!todayFinaleEvent || !activeSeason) return;
    const dedupeKey = `${todayFinaleEvent.id}_${data.userId}_${data.type}_${data.holeNumber ?? 'x'}`;
    const lastCreated = recentNotifRef.current[dedupeKey];
    if (lastCreated && Date.now() - lastCreated < 30000) return;
    recentNotifRef.current[dedupeKey] = Date.now();
    try {
      await setDoc(doc(db, 'finaleNotifications', dedupeKey), {
        ...data,
        eventId: todayFinaleEvent.id,
        seasonId: activeSeason.id,
        expiresAfter: 6,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error creating finale notification:', err);
    }
  };

  // Detect per-hole finale-day events (eagles, birdies, hot/tough streaks) by
  // diffing each player's newly-completed holes against the previous snapshot.
  // All of these use GROSS scoring (hd.grossVsPar = strokes - par), never net.
  useEffect(() => {
    if (!todayFinaleEvent || finaleLeaderboard.length === 0) return;

    const prevData = prevFinaleDataRef.current;
    const isFirstSnapshot = Object.keys(prevData).length === 0;

    if (!isFirstSnapshot) {
      finaleLeaderboard.forEach((player) => {
        const prev = prevData[player.userId] || { holesCompleted: 0 };
        const holeData = player.holeData || {};
        const holesCompleted = player.holesCompleted || 0;

        for (let hole = prev.holesCompleted + 1; hole <= holesCompleted; hole++) {
          const hd = holeData[hole];
          if (!hd || typeof hd.grossVsPar !== 'number') continue;

          if (hd.grossVsPar <= -2) {
            const isAlbatross = hd.grossVsPar <= -3;
            createFinaleNotification({
              type: 'eagle',
              emoji: isAlbatross ? '🦤' : '🦅',
              userId: player.userId,
              playerName: player.playerName,
              holeNumber: hole,
              message: isAlbatross
                ? `${player.playerName} makes an ALBATROSS!! 🦤`
                : `${player.playerName} makes an EAGLE on hole ${hole}! 🦅`,
            });
          } else if (hd.grossVsPar === -1) {
            createFinaleNotification({
              type: 'birdie',
              emoji: '🐦',
              userId: player.userId,
              playerName: player.playerName,
              holeNumber: hole,
              message: `${player.playerName} makes a birdie on hole ${hole}! 🐦`,
            });
          }

          // Hot streak: this hole and the previous hole both gross par-or-better.
          // Only fires once per player per 3 holes.
          const prevHd = holeData[hole - 1];
          if (prevHd && typeof prevHd.grossVsPar === 'number' && hd.grossVsPar <= 0 && prevHd.grossVsPar <= 0) {
            const key = `${player.userId}_hot_streak`;
            const lastHole = holeStreakFiredRef.current[key] ?? -Infinity;
            if (hole - lastHole >= 3) {
              holeStreakFiredRef.current[key] = hole;
              createFinaleNotification({
                type: 'hot_streak',
                emoji: '🔥',
                userId: player.userId,
                playerName: player.playerName,
                holeNumber: hole,
                message: `${player.playerName} is on a hot streak! 🔥`,
              });
            }
          }

          // Tough stretch: 2 of the last 3 holes gross double-bogey or worse.
          // Only fires once per player per 3 holes.
          let doubleOrWorseCount = 0;
          for (let h = hole - 2; h <= hole; h++) {
            const checkHd = h === hole ? hd : holeData[h];
            if (checkHd && typeof checkHd.grossVsPar === 'number' && checkHd.grossVsPar >= 2) doubleOrWorseCount++;
          }
          if (doubleOrWorseCount >= 2) {
            const key = `${player.userId}_tough_stretch`;
            const lastHole = holeStreakFiredRef.current[key] ?? -Infinity;
            if (hole - lastHole >= 3) {
              holeStreakFiredRef.current[key] = hole;
              createFinaleNotification({
                type: 'tough_stretch',
                emoji: '❄️',
                userId: player.userId,
                playerName: player.playerName,
                holeNumber: hole,
                message: `${player.playerName} is having a tough stretch...`,
              });
            }
          }
        }
      });
    }

    // Update snapshot for next comparison
    const newPrevData = {};
    finaleLeaderboard.forEach((player) => {
      newPrevData[player.userId] = {
        holesCompleted: player.holesCompleted || 0,
        holeData: player.holeData || {},
      };
    });
    prevFinaleDataRef.current = newPrevData;
  }, [finaleLeaderboard, todayFinaleEvent]);

  // Detect leaderboard-position events (lead changes, closing in) from the
  // tournament-style finalePlayerData ranking.
  useEffect(() => {
    if (!todayFinaleEvent || finalePlayerData.length === 0) return;

    const participants = finalePlayerData.filter(p => !p.isNonParticipant);
    if (participants.length === 0) return;

    const leader = participants[0];
    const now = Date.now();

    if (leader.holesCompleted >= 6) {
      const prevLeaderId = finalePrevLeaderRef.current;
      if (prevLeaderId && prevLeaderId !== leader.userId) {
        const key = `${leader.userId}_new_leader_fire`;
        const last = recentNotifRef.current[key];
        if (!last || now - last > 60000) {
          recentNotifRef.current[key] = now;
          createFinaleNotification({
            type: 'new_leader',
            emoji: '🏆',
            userId: leader.userId,
            playerName: leader.name,
            holeNumber: leader.holesCompleted,
            message: `🏆 ${leader.name} takes the lead!`,
          });
        }
      }
      finalePrevLeaderRef.current = leader.userId;

      participants.slice(1).forEach((player) => {
        if (player.holesCompleted < 6) return;
        const isClose = player.currentScore <= leader.currentScore + 2;
        const wasClose = !!finalePrevCloseRef.current[player.userId];
        if (isClose && !wasClose) {
          const key = `${player.userId}_closing_in_fire`;
          const last = recentNotifRef.current[key];
          if (!last || now - last > 60000) {
            recentNotifRef.current[key] = now;
            const diff = leader.currentScore - player.currentScore;
            const isTied = diff === 0;
            createFinaleNotification({
              type: 'closing_in',
              emoji: isTied ? '🤝' : '📈',
              userId: player.userId,
              playerName: player.name,
              holeNumber: player.holesCompleted,
              message: isTied
                ? `🤝 ${player.name} has tied the lead!`
                : `📈 ${player.name} is closing in — ${diff} stroke${diff === 1 ? '' : 's'} behind!`,
            });
          }
        }
        finalePrevCloseRef.current[player.userId] = isClose;
      });
    }

  }, [finalePlayerData, todayFinaleEvent]);

  // Push straight onto the visible toast queue (capped at 5 so a burst of
  // notifications can't build up a queue the user has to sit through).
  const enqueueToast = (toast) => {
    setToastQueue((prev) => {
      const next = [...prev, toast];
      return next.length > 5 ? next.slice(next.length - 5) : next;
    });
  };

  // Notifications that fire within 800ms of each other get collapsed into a single
  // "you have N new notifications" toast, rather than queued up back-to-back where
  // the user can't dismiss them fast enough. Each new arrival restarts the window.
  // The pending list lives in a ref, not just state: flushing it queues a toast, and
  // doing that inside a setState updater would fire twice under StrictMode's
  // double-invoked updaters. The state copy is kept in sync for rendering.
  const addToast = (notification) => {
    pendingToastsRef.current = [...pendingToastsRef.current, notification];
    setPendingToasts(pendingToastsRef.current);

    if (bundleTimerRef.current) clearTimeout(bundleTimerRef.current);
    bundleTimerRef.current = setTimeout(() => {
      bundleTimerRef.current = null;
      const pending = pendingToastsRef.current;
      pendingToastsRef.current = [];
      setPendingToasts([]);

      if (pending.length === 0) return;
      if (pending.length === 1) {
        enqueueToast(pending[0]);
      } else {
        enqueueToast({
          id: `bundle_${Date.now()}`,
          type: 'bundle',
          source: 'bundle',
          count: pending.length,
          emoji: '🔔',
          title: 'KWAC Notifications',
          playerName: 'KWAC',
          message: `You have ${pending.length} new notifications`,
          createdAtMs: Date.now(),
        });
      }
    }, 800);
  };

  // A new finale day starts the toast watermark fresh, so a player who was already
  // in the app gets notifications for the new event from its first hole.
  useEffect(() => {
    if (todayFinaleEvent) {
      localStorage.removeItem('finaleNotifLastSeen');
      appLoadTimeRef.current = Date.now();
    }
  }, [todayFinaleEvent?.id]);

  // Real-time listener: finale-day notifications feed the toast queue (and unread shade count)
  useEffect(() => {
    if (!todayFinaleEvent) return;
    const q = query(
      collection(db, 'finaleNotifications'),
      where('eventId', '==', todayFinaleEvent.id),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') return;
        const data = change.doc.data();
        // serverTimestamp() resolves to null on the local optimistic write (e.g. a
        // notification created by this same client). Wait for the follow-up
        // 'modified' event with the server-resolved value instead of dropping it.
        const createdAtMs = data.createdAt?.toMillis ? data.createdAt.toMillis() : null;
        if (createdAtMs === null) return;

        const shadeLastOpened = parseInt(localStorage.getItem('shadeLastOpened') || '0', 10);
        if (createdAtMs > shadeLastOpened && !shadeOpenRef.current) {
          setUnreadShadeCount((c) => c + 1);
        }

        if (createdAtMs <= appLoadTimeRef.current) return;
        if (toastedNotifIdsRef.current.has(change.doc.id)) return;
        toastedNotifIdsRef.current.add(change.doc.id);
        addToast({
          id: change.doc.id,
          source: 'finale',
          emoji: data.emoji,
          title: data.playerName || 'KWAC',
          message: data.message,
          createdAtMs,
        });
      });
    }, (err) => console.error('Error listening to finale notifications:', err));
    return () => unsubscribe();
  }, [todayFinaleEvent]);

  // Real-time listener: regular app notifications also feed the toast queue (and unread shade count)
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') return;
        const data = change.doc.data();
        // serverTimestamp() resolves to null on the local optimistic write (e.g. a
        // notification created by this same client, like your own round_submitted).
        // Wait for the follow-up 'modified' event with the server-resolved value
        // instead of dropping it.
        const createdAtMs = data.timestamp?.toMillis ? data.timestamp.toMillis() : null;
        if (createdAtMs === null) return;

        const shadeLastOpened = parseInt(localStorage.getItem('shadeLastOpened') || '0', 10);
        if (createdAtMs > shadeLastOpened && !shadeOpenRef.current) {
          setUnreadShadeCount((c) => c + 1);
        }

        if (data.read) return;
        if (createdAtMs <= appLoadTimeRef.current) return;
        if (toastedNotifIdsRef.current.has(change.doc.id)) return;
        toastedNotifIdsRef.current.add(change.doc.id);
        addToast({
          id: change.doc.id,
          source: 'regular',
          emoji: data.icon || '📢',
          title: data.title || 'KWAC',
          message: data.message,
          createdAtMs,
        });
      });
    }, (err) => console.error('Error listening to notifications for toast:', err));
    return () => unsubscribe();
  }, [user]);

  // Pull the next toast off the queue whenever nothing is currently showing
  useEffect(() => {
    if (currentToast === null && toastQueue.length > 0) {
      const [next, ...rest] = toastQueue;
      setToastQueue(rest);
      setCurrentToast(next);
      localStorage.setItem('finaleNotifLastSeen', Date.now().toString());
      setToastDismissing(false);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => {
        dismissToast();
      }, 5000);
    }
  }, [toastQueue, currentToast]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (bundleTimerRef.current) clearTimeout(bundleTimerRef.current);
    };
  }, []);

  const dismissToast = () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToastDismissing(true);
    setTimeout(() => {
      setCurrentToast(null);
      setToastDismissing(false);
      if (toastCardRef.current) toastCardRef.current.style.transform = '';
    }, 300);
  };

  const handleToastTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = e.touches[0].clientY;
  };

  const handleToastTouchMove = (e) => {
    touchCurrentY.current = e.touches[0].clientY;
    const deltaY = touchStartY.current - touchCurrentY.current;
    if (deltaY > 0 && toastCardRef.current) {
      toastCardRef.current.style.transform = `translateY(-${deltaY}px)`;
    }
  };

  // A bundle toast stands in for several notifications, so acting on it (tap or
  // swipe up) opens the shade where all of them are actually listed.
  const handleToastActivate = () => {
    const wasBundle = currentToast?.type === 'bundle';
    dismissToast();
    if (wasBundle) openShade();
  };

  const handleToastTouchEnd = () => {
    const deltaY = touchStartY.current - touchCurrentY.current;
    if (deltaY > 50) {
      handleToastActivate();
    } else if (toastCardRef.current) {
      toastCardRef.current.style.transform = '';
    }
  };

  const formatNotifTimeAgo = (ms) => {
    if (!ms) return '';
    const diffMins = Math.floor((Date.now() - ms) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Opens the notification shade: fetches all finale + regular notifications for this user.
  // Each source is fetched independently so a failure in one (e.g. a missing Firestore
  // index or a transient network error) can't wipe out results already fetched from the
  // other — previously a single thrown error here left shadeNotifications at its stale
  // (often empty) value while the badge count stayed unreset, showing "No notifications
  // yet" under a nonzero badge.
  const openShade = async () => {
    let finaleNotifs = [];
    let regularNotifs = [];
    let hadError = false;

    if (todayFinaleEvent) {
      try {
        const fq = query(
          collection(db, 'finaleNotifications'),
          where('eventId', '==', todayFinaleEvent.id),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
        const fSnap = await getDocs(fq);
        // finaleNotifications is a shared, club-wide feed — a doc's userId is whichever
        // player the event happened to, not the viewer, so these can't be deleted per-user
        // without wiping the live feed for everyone else watching. "Delete All" instead
        // hides anything at/before this device's locally-stored clear point.
        const clearedAt = parseInt(localStorage.getItem('finaleFeedClearedAt') || '0', 10);
        finaleNotifs = fSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              source: 'finale',
              emoji: data.emoji,
              title: data.playerName || 'KWAC',
              message: data.message,
              timeMs: data.createdAt?.toMillis ? data.createdAt.toMillis() : Date.now(),
            };
          })
          .filter((n) => n.timeMs > clearedAt);
      } catch (err) {
        console.error('Error loading finale notifications:', err);
        hadError = true;
      }
    }

    if (user) {
      try {
        const rq = query(
          collection(db, 'notifications'),
          where('userId', '==', user.uid),
          orderBy('timestamp', 'desc'),
          limit(100)
        );
        const rSnap = await getDocs(rq);
        regularNotifs = rSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            source: 'regular',
            emoji: data.icon || '📢',
            title: data.title || 'KWAC',
            message: data.message,
            timeMs: data.timestamp?.toMillis ? data.timestamp.toMillis() : Date.now(),
          };
        });
      } catch (err) {
        console.error('Error loading notifications:', err);
        hadError = true;
      }
    }

    const merged = [...finaleNotifs, ...regularNotifs].sort((a, b) => b.timeMs - a.timeMs);
    setShadeNotifications(merged);
    setShadeLoadError(hadError);
    // Only mark everything as "seen" if the load actually succeeded — otherwise a failed
    // fetch would silently discard the unread badge for notifications the user never saw.
    if (!hadError) {
      localStorage.setItem('shadeLastOpened', String(Date.now()));
      setUnreadShadeCount(0);
    }
    setShadeOpen(true);
  };

  const closeShade = () => {
    setShadeDismissing(true);
    setTimeout(() => {
      setShadeOpen(false);
      setShadeDismissing(false);
      setShadeDeleteConfirm(false);
    }, 350);
  };

  const deleteAllShadeNotifications = async () => {
    if (shadeNotifications.length === 0) return;
    try {
      // Personal notifications belong to this user, so those are actually deleted.
      // Finale-feed items are shared with everyone watching the event — deleting those
      // would wipe the live feed for other players too, so this device just stops
      // showing anything up to now instead of touching the shared docs.
      const ownNotifs = shadeNotifications.filter((n) => n.source === 'regular');
      const finaleNotifs = shadeNotifications.filter((n) => n.source === 'finale');

      if (ownNotifs.length > 0) {
        const batch = writeBatch(db);
        ownNotifs.forEach((n) => batch.delete(doc(db, 'notifications', n.id)));
        await batch.commit();
      }

      if (finaleNotifs.length > 0) {
        const latestMs = Math.max(...finaleNotifs.map((n) => n.timeMs));
        localStorage.setItem('finaleFeedClearedAt', String(latestMs));
      }

      setShadeNotifications([]);
      setUnreadShadeCount(0);
    } catch (err) {
      console.error('Error deleting notifications:', err);
    } finally {
      setShadeDeleteConfirm(false);
    }
  };

  // Global swipe gesture: swipe down from the very top edge opens the shade, swipe up closes it
  useEffect(() => {
    if (!isMobile) return;
    let startY = 0;
    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e) => {
      const endY = e.changedTouches[0].clientY;
      const deltaY = endY - startY;
      if (startY < 30 && deltaY > 60 && !shadeOpenRef.current) {
        openShade();
      }
      if (shadeOpenRef.current && deltaY < -60) {
        closeShade();
      }
    };
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, todayFinaleEvent, user]);

  const dismissRecapBanner = () => {
    setBannerVisible(false);
    setTimeout(() => setShowRecapBanner(false), 400);
    if (activeRecapSeasonId) localStorage.setItem(`kwac_recap_seen_${activeRecapSeasonId}`, '1');
  };

  // Load season leaderboard
  useEffect(() => {
    if (!activeSeason) return;

    const loadSeasonLeaderboard = async () => {
      try {
        setLeaderboardLoading(true);
        
        const roundsQuery = query(
          collection(db, 'rounds'),
          where('seasonId', '==', activeSeason.id),
          where('status', '==', 'approved')
        );

        const roundsSnapshot = await getDocs(roundsQuery);
        const allRoundsData = roundsSnapshot.docs.map(d => d.data()).filter(r => !r.isFinale);

        // Group by user
        const userStats = {};

        allRoundsData.forEach(round => {
  const userId = round.userId;
  
  if (!userStats[userId]) {
    userStats[userId] = {
      userId: userId,
      totalPoints: 0,
      roundsPlayed: 0,
      bestRound: 0,
      seasonLow: null,
      totalGross: 0,
      allScores: []
    };
  }
  
  const stat = userStats[userId];
  stat.totalPoints += round.totalStableford || 0;
  stat.totalGross += round.totalGross || 0;
  stat.roundsPlayed += 1;
  stat.allScores.push(round.totalStableford || 0); 
  
  if (round.totalStableford > stat.bestRound) {
    stat.bestRound = round.totalStableford;
  }
  if (stat.seasonLow === null || round.totalGross < stat.seasonLow) {
    stat.seasonLow = round.totalGross;
  }
});
        
       // Convert to array and calculate averages with drop system
const leaderboard = Object.values(userStats).map(stats => {
  // Determine how many worst rounds to drop based on total rounds
  let dropCount = 0;
  if (stats.roundsPlayed >= 25) dropCount = 4;
  else if (stats.roundsPlayed >= 20) dropCount = 3;
  else if (stats.roundsPlayed >= 15) dropCount = 2;
  else if (stats.roundsPlayed >= 10) dropCount = 1;
  
  // Calculate average with drops
  let adjustedTotal = stats.totalPoints;
  let countingRounds = stats.roundsPlayed;
  
  if (dropCount > 0 && stats.allScores && stats.allScores.length > 0) {
    // Sort scores ascending (worst first)
    const sortedScores = [...stats.allScores].sort((a, b) => a - b);
    
    // Drop the worst N scores
    const droppedScores = sortedScores.slice(0, dropCount);
    const droppedTotal = droppedScores.reduce((sum, score) => sum + score, 0);
    
    adjustedTotal = stats.totalPoints - droppedTotal;
    countingRounds = stats.roundsPlayed - dropCount;
  }
  
  return {
    ...stats,
    averagePoints: countingRounds > 0 ? (adjustedTotal / countingRounds).toFixed(2) : '0.00',
    droppedRounds: dropCount,
    countingRounds: countingRounds
  };
});
        
       // Sort by average points (descending) - rewards consistency
leaderboard.sort((a, b) => parseFloat(b.averagePoints) - parseFloat(a.averagePoints));
        
        setSeasonLeaderboard(leaderboard);

        // Compute position changes by comparing current snapshot vs previous snapshot
        // (all rounds except those approved on the most recent approval date)
        const approvalDates = allRoundsData
          .map(r => r.approvedAt ? r.approvedAt.split('T')[0] : null)
          .filter(Boolean);
        const mostRecentDate = approvalDates.length > 0
          ? [...approvalDates].sort().pop()
          : null;

        const previousRounds = mostRecentDate
          ? allRoundsData.filter(r =>
              !r.approvedAt || r.approvedAt.split('T')[0] !== mostRecentDate
            )
          : [];

        const previousPositions = previousRounds.length > 0
          ? computePositionsFromRounds(previousRounds)
          : {};

        const changes = {};
        leaderboard.forEach((player, index) => {
          const currentPos = index + 1;
          const prevPos = previousPositions[player.userId];
          const positionDiff = prevPos !== undefined ? prevPos - currentPos : 'new';
          changes[player.userId] = positionDiff;

          // Notify the current user of significant position changes
          if (user && player.userId === user.uid && typeof positionDiff === 'number') {
            if (positionDiff >= 2) {
              createNotification(user.uid, 'position_change', {
                direction: 'up',
                newPosition: currentPos,
                change: positionDiff,
              }).catch(e => console.error('Error sending position change notification:', e));
            } else if (positionDiff <= -3) {
              createNotification(user.uid, 'position_change', {
                direction: 'down',
                newPosition: currentPos,
                change: positionDiff,
              }).catch(e => console.error('Error sending position change notification:', e));
            }
          }
        });
        setDashboardPositionChanges(changes);

      } catch (error) {
        console.error('Error loading season leaderboard:', error);
      } finally {
        setLeaderboardLoading(false);
      }

    };

   loadSeasonLeaderboard();
  }, [activeSeason, activeSeason?.id, user?.uid]);

  // Load Hall of Fame (Season Champions & Finale Winners)
useEffect(() => {
  const loadHallOfFame = async () => {
    try {
      const trophiesQuery = query(
        collection(db, 'special_trophies'),
        where('type', 'in', ['season_champion', 'finale_winner', 'hall_of_fame'])
      );
      const trophiesSnapshot = await getDocs(trophiesQuery);
      
      const champions = trophiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by year (most recent first), then by type
      champions.sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return a.type === 'season_champion' ? -1 : 1;
      });
      
      setHallOfFame(champions);
    } catch (error) {
      console.error('Error loading Hall of Fame:', error);
    }
    
  };

  

  loadHallOfFame();
}, []);
// Load Season Records
useEffect(() => {
  const loadSeasonRecords = async () => {
    if (!activeSeason) return;
    
    try {
      setRecordsLoading(true);
      
      const roundsQuery = query(
        collection(db, 'rounds'),
        where('seasonId', '==', activeSeason.id),
        where('status', '==', 'approved')
      );
      const roundsSnapshot = await getDocs(roundsQuery);
      const seasonRecordRounds = roundsSnapshot.docs.map(d => d.data()).filter(r => !r.isFinale);

      // Track records
      let bestStableford = { userId: null, value: 0 };
      let lowestGross = { userId: null, value: 999 };
      let birdieCount = {};
      let girStats = {};

      seasonRecordRounds.forEach(round => {
        const userId = round.userId;
        
        // Best Stableford
        if (round.totalStableford > bestStableford.value) {
          bestStableford = { userId, value: round.totalStableford };
        }
        
        // Lowest Gross
        if (round.totalGross < lowestGross.value) {
          lowestGross = { userId, value: round.totalGross };
        }
        
        // Birdie tracking
        if (round.holeData) {
          if (!birdieCount[userId]) birdieCount[userId] = 0;
          
          Object.entries(round.holeData).forEach(([holeNum, holeData]) => {
            if (!holeData.strokes) return;
            const courseHoles = getHolesByCourse(round.course);
            const holeInfo = courseHoles.find(h => h.number === parseInt(holeNum));
            if (!holeInfo) return;
            
            if (holeData.strokes === holeInfo.par - 1) {
              birdieCount[userId]++;
            }
          });
        }
        
        // GIR tracking
        if (!girStats[userId]) {
          girStats[userId] = { total: 0, count: 0 };
        }
        girStats[userId].total += round.greensInRegulation || 0;
        girStats[userId].count += 9;
      });
      
      // Find best GIR %
      let bestGIR = { userId: null, value: 0 };
      Object.entries(girStats).forEach(([userId, stats]) => {
        const percentage = (stats.total / stats.count) * 100;
        if (percentage > bestGIR.value) {
          bestGIR = { userId, value: Math.round(percentage) };
        }
      });
      
      // Find most birdies
      let mostBirdies = { userId: null, value: 0 };
      Object.entries(birdieCount).forEach(([userId, count]) => {
        if (count > mostBirdies.value) {
          mostBirdies = { userId, value: count };
        }
      });
      
      const records = [
        { icon: '🏆', label: 'Best Round', userId: bestStableford.userId, value: `${bestStableford.value} pts` },
        { icon: '⛳', label: 'Lowest Gross', userId: lowestGross.userId, value: `${lowestGross.value} strokes` },
        { icon: '🐦', label: 'Most Birdies', userId: mostBirdies.userId, value: `${mostBirdies.value} birdies` },
        { icon: '🎯', label: 'Best GIR %', userId: bestGIR.userId, value: `${bestGIR.value}%` }
      ].filter(r => r.userId); // Only include records that exist
      
      setSeasonRecords(records);
    } catch (error) {
      console.error('Error loading season records:', error);
    } finally {
      setRecordsLoading(false);
    }
  };
  
  loadSeasonRecords();
}, [activeSeason]);

// Session tracking
useEffect(() => {
  if (!user) return;

  const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes

  let currentSessionId;
  let activityInterval;
  let lastActivity = Date.now();
  let lastPresenceUpdate = Date.now();

  const initSession = async () => {
    currentSessionId = await startSession(user.uid);
    setSessionId(currentSessionId);
    await updateUserPresence(user.uid, true, 'Dashboard');

    // Check for idle every 60 seconds
    activityInterval = setInterval(async () => {
      if (Date.now() - lastActivity > IDLE_TIMEOUT) {
        // Session idle — end current and start a new one when user returns
        await endSession(currentSessionId);
        currentSessionId = await startSession(user.uid);
        setSessionId(currentSessionId);
        await updateUserPresence(user.uid, true, 'Dashboard');
        lastActivity = Date.now();
      } else {
        updateSessionActivity(currentSessionId);
      }
    }, 60000);
  };

  const handleActivity = () => {
    lastActivity = Date.now();
    const now = Date.now();
    if (now - lastPresenceUpdate > 60000) {
      lastPresenceUpdate = now;
      updateUserPresence(user.uid, true, 'Dashboard');
    }
  };

  const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
  events.forEach(e => window.addEventListener(e, handleActivity));

  initSession();

  // Cleanup on unmount
  return () => {
    if (currentSessionId) {
      endSession(currentSessionId);
      updateUserPresence(user.uid, false, null);
    }
    if (activityInterval) {
      clearInterval(activityInterval);
    }
    events.forEach(e => window.removeEventListener(e, handleActivity));
  };
}, [user]);

// Track page views when modals open
useEffect(() => {
  if (!user) return;
  
  if (showScoreEntry) trackPageView(user.uid, 'Score Entry');
  if (showMyRounds) trackPageView(user.uid, 'My Rounds');
  if (showLeaderboard) trackPageView(user.uid, 'Leaderboard');
  if (showFunStats) trackPageView(user.uid, 'Fun Stats');
  if (showMyProfile) trackPageView(user.uid, 'My Profile');
  if (showAdminPanel) trackPageView(user.uid, 'Admin Panel');
  if (showPlayers) trackPageView(user.uid, 'Players');
}, [user, showScoreEntry, showMyRounds, showLeaderboard, showFunStats, showMyProfile, showAdminPanel, showPlayers]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log('User logged out');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleOpenSettings = (tab) => {
    setSettingsTab(tab);
    setShowSettings(true);
    setShowProfileDropdown(false);
  };

  const reloadUserProfile = async () => {
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) setUserProfile(userDoc.data());
    } catch (e) { console.error('Error reloading profile:', e); }
  };

  useEffect(() => {
    if (!showProfileDropdown) return;
    const handler = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showProfileDropdown]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const lastY = lastScrollYRef.current;
        if (currentY <= 10) {
          setHeaderVisible(true);
        } else if (currentY > lastY + 5) {
          setHeaderVisible(false);
        } else if (currentY < lastY - 5) {
          setHeaderVisible(true);
        }
        lastScrollYRef.current = currentY;
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFinishRound = () => {
    setShowScoreEntry(true);
  };

  const handleDismissBanner = (e) => {
    e.stopPropagation();
    setShowRoundBanner(false);
  };

  const handleBeerEntry = async (beers) => {
    if (pendingBeer && beers !== null) {
      try {
        await updateDoc(doc(db, 'rounds', pendingBeer.roundId), {
          beersAfterRound: beers
        });
      } catch (error) {
        console.error('Error saving beer count:', error);
      }
    }
    localStorage.removeItem('pendingBeerEntry');
    setPendingBeer(null);
  };

  const handleEditRound = (roundData) => {
    setEditRoundData(roundData);
    setShowMyRounds(false);
    setShowEditScoreEntry(true);
  };

  const handleLogoClick = () => {
    window.location.reload();
  };

  const getUserName = (userId) => {
    return users[userId]?.name || 'Unknown';
  };

  const visibleLeaderboard = leaderboardExpanded
    ? seasonLeaderboard
    : seasonLeaderboard.slice(0, 8);

  useEffect(() => { setLeaderboardExpanded(false); }, [activeSeason?.id]);

  const getMedalEmoji = (position) => {
    if (position === 0) return '🥇';
    if (position === 1) return '🥈';
    if (position === 2) return '🥉';
    return '';
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.headerBrand}>
  <img src={logo} alt="KWAC" style={styles.logo} />
</div>
        <h2 style={styles.loadingText}>...</h2>
        
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  if (userProfile?.status === 'pending') {
    return (
      <div style={styles.pendingContainer}>
        <div style={styles.pendingCard}>
          <h1 style={styles.pendingTitle}>⏳ Membership Pending</h1>
          <p style={styles.pendingText}>
            Thank you for requesting membership, <strong>{userProfile.name}</strong>!
          </p>
          <p style={styles.pendingText}>
            Your account is awaiting approval from a club administrator.
            You'll receive an email once your membership has been approved.
          </p>
          <button onClick={handleLogout} style={styles.logoutButtonAlt}>
            Logout
          </button>
        </div>
      </div>
    );
  }

  if (userProfile?.status === 'denied') {
    return (
      <div style={styles.pendingContainer}>
        <div style={styles.pendingCard}>
          <h1 style={styles.pendingTitle}>❌ Membership Denied</h1>
          <p style={styles.pendingText}>
            Your membership request was not approved.
          </p>
          <button onClick={handleLogout} style={styles.logoutButtonAlt}>
            Logout
          </button>
        </div>
      </div>
    );
  }

  const canManage = ['admin', 'moderator', 'officer'].includes(userProfile?.role);
  const seasonChampionIds = new Set(hallOfFame.filter(h => h.type === 'season_champion').map(h => h.userId));

  return (
    <div style={styles.container}>
      <div style={styles.statusBarFill} />
      <div style={{ height: headerHeight }} />
      <div
        ref={setHeaderRef}
        style={{
          ...styles.header,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)'
        }}
      >
        <div style={styles.headerBrand}>
          <div style={styles.headerBrand}>
  <img src={logo} alt="KWAC" style={{ ...styles.logo, cursor: 'pointer' }} onClick={handleLogoClick} />
</div>
        </div>
        <div style={styles.userInfo}>
  <NotificationBell userId={user.uid} />
  <div ref={profileDropdownRef} style={{ position: 'relative' }}>
    <div
      style={styles.profileButton}
      onClick={() => setShowProfileDropdown(v => !v)}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {userProfile?.photoURL ? (
        <img src={userProfile.photoURL} alt="Profile" style={styles.profilePhoto} />
      ) : (
        <div style={styles.profilePhotoPlaceholder}>
          {userProfile?.name?.charAt(0) || user.email?.charAt(0) || '?'}
        </div>
      )}
    </div>
    {showProfileDropdown && (
      <div style={{
        position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 1500,
        backgroundColor: '#1e1e1e', border: '1px solid #2d2d2d', borderRadius: '10px',
        minWidth: '180px', padding: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
      }}>
        {/* caret */}
        <div style={{
          position: 'absolute', top: '-6px', right: '12px',
          width: 0, height: 0,
          borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
          borderBottom: '6px solid #2d2d2d',
        }} />
        <div style={{
          position: 'absolute', top: '-5px', right: '12px',
          width: 0, height: 0,
          borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
          borderBottom: '6px solid #1e1e1e',
        }} />
        {[
          { icon: '👤', label: 'My Profile', action: () => { setViewingProfileId(user.uid); setShowMyProfile(true); setShowProfileDropdown(false); } },
          { icon: '✏️', label: 'Edit Profile', action: () => handleOpenSettings('editProfile') },
          { icon: '🔑', label: 'Change Password', action: () => handleOpenSettings('changePassword') },
          { icon: '🔔', label: 'Notification Preferences', action: () => handleOpenSettings('notifications') },
        ].map((item, i) => (
          <div
            key={i}
            onClick={item.action}
            onMouseEnter={() => setDropdownHover(i)}
            onMouseLeave={() => setDropdownHover(null)}
            style={{
              padding: '10px 14px', borderRadius: '7px', cursor: 'pointer',
              fontSize: '14px', fontWeight: '500', color: '#ffffff',
              display: 'flex', alignItems: 'center', gap: '10px',
              backgroundColor: dropdownHover === i ? '#2d2d2d' : 'transparent',
              transition: 'background-color 0.15s ease',
            }}
          >
            <span>{item.icon}</span>{item.label}
          </div>
        ))}
        <div style={{ height: '1px', backgroundColor: '#2d2d2d', margin: '4px 0' }} />
        <div
          onClick={() => { handleLogout(); setShowProfileDropdown(false); }}
          onMouseEnter={() => setDropdownHover('logout')}
          onMouseLeave={() => setDropdownHover(null)}
          style={{
            padding: '10px 14px', borderRadius: '7px', cursor: 'pointer',
            fontSize: '14px', fontWeight: '500', color: '#ef4444',
            display: 'flex', alignItems: 'center', gap: '10px',
            backgroundColor: dropdownHover === 'logout' ? '#2d2d2d' : 'transparent',
            transition: 'background-color 0.15s ease',
          }}
        >
          <span>🚪</span>Log Out
        </div>
      </div>
    )}
  </div>
</div>
      </div>

      {showRoundBanner && savedRound && (
        <div style={styles.bannerContainer}>
          <div style={styles.banner} onClick={handleFinishRound}>
            <div style={styles.bannerIcon}>🏌️</div>
            <div style={styles.bannerContent}>
              <div style={styles.bannerTitle}>Round In Progress</div>
              <div style={styles.bannerSubtitle}>
                {savedRound.selectedCourse} • Hole {savedRound.currentHoleIndex + 1}/{getHolesByCourse(savedRound.selectedCourse).length}
              </div>
            </div>
            <div style={styles.bannerAction}>
              <span style={styles.bannerButton}>Finish Round →</span>
            </div>
            <button
              style={styles.bannerClose}
              onClick={handleDismissBanner}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {pendingBeer && (
        <div style={styles.bannerContainer}>
          <div style={{ ...styles.banner, cursor: 'default', flexWrap: 'wrap', gap: '16px' }}>
            <div style={styles.bannerIcon}>🍺</div>
            <div style={styles.bannerContent}>
              <div style={styles.bannerTitle}>Post-Round Refreshments</div>
              <div style={styles.bannerSubtitle}>How many beers did you have after your round?</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n} onClick={() => handleBeerEntry(n)} style={styles.beerButton}>{n}</button>
              ))}
              <button onClick={() => handleBeerEntry(11)} style={styles.beerButton}>10+</button>
            </div>
            <button
              style={styles.bannerClose}
              onClick={() => handleBeerEntry(null)}
              aria-label="Skip"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div style={styles.content}>
        <div style={styles.welcomeSection}>
          <h2 style={styles.dashboardTitle}>🎯 Dashboard</h2>
          <p style={styles.welcomeText}>Welcome back, {userProfile?.name?.split(' ')[0] || 'Golfer'}!</p>
        </div>

        {/* 4-Card Stat Grid */}
<div style={styles.statsGrid}>
  <div style={styles.statCard}>
    <div style={styles.statCardHeader}>
      <div style={styles.statCardLabel}>SEASON RANK</div>
      <div style={styles.statCardIcon}>🏆</div>
    </div>
    <div style={styles.statCardValue}>
      #{seasonLeaderboard.findIndex(p => p.userId === user.uid) + 1 || '-'}
    </div>
    <div style={styles.statCardFooter}>
      {seasonLeaderboard.length > 0 && `of ${seasonLeaderboard.length} players`}
    </div>
  </div>

  <div style={styles.statCard}>
    <div style={styles.statCardHeader}>
      <div style={styles.statCardLabel}>AVG SCORE</div>
      <div style={styles.statCardIcon}>🎯</div>
    </div>
    <div style={styles.statCardValue}>
      {parseFloat(seasonLeaderboard.find(p => p.userId === user.uid)?.averagePoints || 0).toFixed(2)}
    </div>
    <div style={styles.statCardFooter}>points per round</div>
  </div>

  <div style={styles.statCard}>
    <div style={styles.statCardHeader}>
      <div style={styles.statCardLabel}>SEASON LOW</div>
      <div style={styles.statCardIcon}>📈</div>
    </div>
    <div style={styles.statCardValue}>
      {seasonLeaderboard.find(p => p.userId === user.uid)?.seasonLow || '-'}
    </div>
    <div style={styles.statCardFooter}>season low</div>
  </div>

  <div style={styles.statCard}>
    <div style={styles.statCardHeader}>
      <div style={styles.statCardLabel}>ROUNDS PLAYED</div>
      <div style={styles.statCardIcon}>⛳</div>
    </div>
    <div style={styles.statCardValue}>
      {seasonLeaderboard.find(p => p.userId === user.uid)?.roundsPlayed || '0'}
    </div>
    <div style={styles.statCardFooter}>this season</div>
  </div>
</div>

      {/* Leaderboard & Hall of Fame Side by Side */}
{/* Desktop: Leaderboard left, Cards right. Mobile: Leaderboard top, Cards bottom */}
<div style={styles.mainLayout} className="main-layout">
  
  {/* Left column: Leaderboard + Hall of Fame */}
  <div style={styles.leftColumn}>
    
    {/* Season Finale — Live leaderboard, shown only on finale day */}
    {todayFinaleEvent && (
      <div style={{
        backgroundColor: colors.bgCard,
        border: `2px solid ${colors.gold}`,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '16px',
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${colors.borderSubtle}`,
        }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: colors.gold }}>
            🏆 Season Finale — Live
          </div>
          <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>
            {todayFinaleEvent.name}
          </div>
        </div>

        {finalePlayerData.length === 0 ? (
          <div style={{ fontSize: '14px', color: colors.textTertiary, textAlign: 'center', padding: '30px' }}>
            🏌️ Waiting for scores...
          </div>
        ) : (() => {
          const participants = finalePlayerData.filter(p => !p.isNonParticipant);
          const nonParticipants = finalePlayerData.filter(p => p.isNonParticipant);
          const visibleParticipants = finaleLeaderboardExpanded ? participants : participants.slice(0, 8);

          const renderRow = (player, index) => {
            const isChampion = seasonChampionIds.has(player.userId);
            const isCurrentUser = player.userId === user?.uid;
            const scoreColor = player.isNonParticipant
              ? '#4b5563'
              : getScoreColor(player.currentScore, player.holesCompleted);
            const thru = player.holesCompleted === 0 ? '' : player.holesCompleted === 18 ? 'F' : String(player.holesCompleted);
            const todayColor = player.isNonParticipant
              ? '#4b5563'
              : getScoreColor(player.runningOverUnder, player.holesCompleted);

            return (
              <div key={player.userId} style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr 52px 52px 36px',
                gap: '8px',
                padding: '12px 16px',
                alignItems: 'center',
                backgroundColor: player.isNonParticipant ? '#0a0a0a' : (index % 2 === 0 ? colors.bgDark : '#1a1a1a'),
                opacity: player.isNonParticipant ? 0.5 : 1,
                borderLeft: isCurrentUser ? `2px solid ${colors.gold}` : '2px solid transparent',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: player.isNonParticipant ? '#4b5563' : colors.textSecondary, textAlign: 'center' }}>
                  {player.isNonParticipant ? '—' : (index < 3 ? getMedalEmoji(index) : `${index + 1}.`)}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    minWidth: 0,
                    cursor: 'pointer',
                    color: player.isNonParticipant ? '#4b5563' : (isCurrentUser ? colors.gold : (isChampion ? colors.gold : colors.textPrimary)),
                  }}
                  onClick={() => {
                    setViewingProfileId(player.userId);
                    setShowMyProfile(true);
                  }}
                >
                  {isChampion && !player.isNonParticipant && <span style={{ fontSize: '11px', flexShrink: 0 }}>👑</span>}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {player.name}
                  </span>
                </div>
                <div style={{ fontSize: '16px', fontWeight: '700', textAlign: 'center', color: scoreColor }}>
                  {formatFinaleScore(player.currentScore)}
                </div>
                <div style={{ fontSize: '13px', fontWeight: '600', textAlign: 'center', color: todayColor }}>
                  {player.isNonParticipant ? '-' : formatTodayScore(player.runningOverUnder, player.holesCompleted)}
                </div>
                <div style={{ fontSize: '12px', color: colors.textTertiary, textAlign: 'center' }}>
                  {thru}
                </div>
              </div>
            );
          };

          return (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr 52px 52px 36px',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: colors.bgDark,
              }}>
                {['#', 'Player', 'Score', 'Today', 'Thru'].map((label, i) => (
                  <div key={i} style={{
                    fontSize: '10px',
                    fontWeight: '700',
                    color: colors.textTertiary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: i === 1 ? 'left' : 'center',
                  }}>
                    {label}
                  </div>
                ))}
              </div>
              {visibleParticipants.map((player, index) => renderRow(player, index))}
              {nonParticipants.length > 0 && (
                <div style={{
                  borderTop: '1px dashed #2d2d2d',
                  padding: '6px 16px',
                  fontSize: '11px',
                  color: '#4b5563',
                }}>
                  Not participating
                </div>
              )}
              {nonParticipants.map((player, index) => renderRow(player, index))}
            </div>
          );
        })()}
        {finalePlayerData.filter(p => !p.isNonParticipant).length > 8 && (
          <button
            onClick={() => setFinaleLeaderboardExpanded(prev => !prev)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'transparent',
              color: colors.gold,
              border: `1px solid ${colors.borderSubtle}`,
              borderTop: 'none',
              borderRadius: '0 0 12px 12px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              letterSpacing: '0.03em',
              transition: 'all 0.2s ease',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {finaleLeaderboardExpanded
              ? '▲ Show less'
              : `▼ Show all ${finalePlayerData.filter(p => !p.isNonParticipant).length} players`}
          </button>
        )}
      </div>
    )}

    {/* Full Leaderboard (always expanded on desktop) */}
    {seasonLeaderboardCollapsed ? (
      <div style={{
        backgroundColor: colors.bgCard,
        border: `1px solid ${colors.borderSubtle}`,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        marginBottom: '16px',
      }}>
        <button
          onClick={() => setSeasonLeaderboardCollapsed(false)}
          style={{ ...styles.statsToggleGold, borderBottom: 'none' }}
        >
          <div style={styles.toggleLeft}>
            <span style={styles.toggleIcon}>🏆</span>
            <span style={styles.toggleTextGold}>KWAC Season Leaderboard</span>
          </div>
          <span style={styles.toggleArrowGold}>▼</span>
        </button>
      </div>
    ) : (
    <div style={styles.leaderboardWidget}>
      <div
        onClick={() => setSeasonLeaderboardCollapsed(true)}
        style={{ ...styles.leaderboardHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}
      >
        <div>
          <h3 style={styles.leaderboardHeaderTitle}>
            🏆 {activeSeason ? activeSeason.name : 'Season'} Leaderboard
          </h3>
          <p style={styles.leaderboardSubtitle}>Ranked by average points per round</p>
        </div>
        <span style={{ ...styles.toggleArrowGold, flexShrink: 0 }}>
          ▲
        </span>
      </div>

      <div style={styles.leaderboardScrollable}>
      {leaderboardLoading ? (
  <div style={styles.statsLoading}>Loading leaderboard...</div>
) : !activeSeason ? (
          <div style={styles.statsEmpty}>No active season</div>
        ) : seasonLeaderboard.length === 0 ? (
          <div style={styles.statsEmpty}>No rounds yet</div>
        ) : (
          <div style={styles.statsList}>
            {visibleLeaderboard.map((player, index) => {
              const isChampion = seasonChampionIds.has(player.userId);
              const diff = dashboardPositionChanges[player.userId];
              const changeColor = diff > 0 ? '#10b981' : diff < 0 ? '#ef4444' : '#6b7280';
              const arrow = diff > 0 ? '↑' : diff < 0 ? '↓' : '-';
              return (
              <div key={player.userId} style={{
                ...styles.statsRow,
                ...(isChampion ? { borderLeft: '2px solid rgba(212,175,55,0.55)', backgroundColor: 'rgba(212,175,55,0.05)' } : {})
              }}>
                <div style={styles.statsPosition}>
                  {index < 3 ? getMedalEmoji(index) : `${index + 1}.`}
                </div>
                <div
                  style={{
                    ...styles.statsPlayer,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '4px',
                    minWidth: 0,
                    ...(isChampion ? { color: '#d4af37' } : {})
                  }}
                  onClick={() => {
                    setViewingProfileId(player.userId);
                    setShowMyProfile(true);
                  }}
                >
                  {isChampion && (
                    <span style={{
                      fontSize: '11px',
                      flexShrink: 0,
                      lineHeight: '1.4',
                      marginTop: '1px'
                    }}>
                      👑
                    </span>
                  )}
                  <span style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    lineHeight: '1.4'
                  }}>
                    {getUserName(player.userId)}
                  </span>
                </div>
                <div style={styles.statsValue}>{parseFloat(player.averagePoints).toFixed(2)}</div>
                <div style={{textAlign: 'center', fontSize: '13px', fontWeight: '600', color: changeColor}}>
                  {arrow}{diff !== 0 && diff !== 'new' && diff !== undefined ? ` ${Math.abs(diff)}` : ''}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      {seasonLeaderboard.length > 8 && (
        <button
          onClick={() => setLeaderboardExpanded(prev => !prev)}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: 'transparent',
            color: '#d4af37',
            border: '1px solid #2d2d2d',
            borderTop: 'none',
            borderRadius: '0 0 12px 12px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            letterSpacing: '0.03em',
            transition: 'all 0.2s ease',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {leaderboardExpanded
            ? '▲ Show less'
            : `▼ Show all ${seasonLeaderboard.length} players`}
        </button>
      )}
    </div>
    )}

    {/* Hall of Fame */}
    {hallOfFame.length > 0 && (
      <div style={styles.hofWidget}>
        <button 
          onClick={() => setHallOfFameExpanded(!hallOfFameExpanded)}
          style={styles.statsToggleGold}
        >
          <div style={styles.toggleLeft}>
            <span style={styles.toggleIcon}>👑</span>
            <span style={styles.toggleTextGold}>Hall of Fame</span>
          </div>
          <span style={styles.toggleArrowGold}>
            {hallOfFameExpanded ? '▲' : '▼'}
          </span>
        </button>

        {hallOfFameExpanded && (
          <div style={styles.statsContent}>
            <div style={styles.statsList}>
              {hallOfFame.map((champion) => {
                const photoURL = users[champion.userId]?.photoURL;
                const initial = users[champion.userId]?.name?.charAt(0) || '?';

                return (
                  <div
                    key={champion.id}
                    style={styles.championRow}
                    onClick={() => {
                      setViewingProfileId(champion.userId);
                      setShowMyProfile(true);
                    }}
                  >
                    <div style={styles.championPhotoSmall}>
                      {photoURL ? (
                        <img
                          src={photoURL}
                          alt="Champion"
                          style={styles.championPhotoImgSmall}
                        />
                      ) : (
                        <div style={styles.championPhotoPlaceholderSmall}>
                          {initial}
                        </div>
                      )}
                    </div>
                    <div style={styles.championInfoSmall}>
                      <div style={styles.championNameSmall}>
                        {getUserName(champion.userId)}
                      </div>
                      <div style={styles.championYearSmall}>
                        {champion.type === 'season_champion' ? '🏆' : '⭐'} {champion.year}
                        {champion.note ? ` · ${champion.note}` : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    )}

    {/* Season Records */}
{seasonRecords.length > 0 && (
  <div style={styles.recordsWidget}>
    <button 
      onClick={() => setRecordsExpanded(!recordsExpanded)}
      style={styles.statsToggleGold}
    >
      <div style={styles.toggleLeft}>
        <span style={styles.toggleIcon}>🏆</span>
        <span style={styles.toggleTextGold}>Season Records</span>
      </div>
      <span style={styles.toggleArrowGold}>
        {recordsExpanded ? '▲' : '▼'}
      </span>
    </button>

    {recordsExpanded && (
      <div style={styles.statsContent}>
        {recordsLoading ? (
          <div style={styles.statsLoading}>Loading...</div>
        ) : (
          <div style={styles.statsList}>
            {seasonRecords.map((record, index) => {
              const photoURL = users[record.userId]?.photoURL;
              const initial = users[record.userId]?.name?.charAt(0) || '?';

              return (
                <div
                  key={index}
                  style={styles.recordRow}
                  onClick={() => {
                    setViewingProfileId(record.userId);
                    setShowMyProfile(true);
                  }}
                >
                  <div style={styles.recordIcon}>{record.icon}</div>
                  <div style={styles.championPhotoSmall}>
                    {photoURL ? (
                      <img
                        src={photoURL}
                        alt="Record holder"
                        style={styles.championPhotoImgSmall}
                      />
                    ) : (
                      <div style={styles.championPhotoPlaceholderSmall}>
                        {initial}
                      </div>
                    )}
                  </div>
                  <div style={styles.recordInfo}>
                    <div style={styles.recordLabel}>{record.label}</div>
                    <div style={styles.recordPlayer}>
                      {getUserName(record.userId)}
                    </div>
                  </div>
                  <div style={styles.recordValue}>{record.value}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}
  </div>
)}
  </div>

  {/* Right column: Dashboard cards */}
  <div style={styles.rightColumn}>
    <div style={styles.grid}>
          <button 
            style={styles.cardButton}
            onClick={() => setShowScoreEntry(true)}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={styles.cardIcon}>📝</div>
            <h3 style={styles.cardTitle}>Submit Score</h3>
            <p style={styles.cardText}>Enter your round scores</p>
          </button>
          
          <button 
            style={styles.cardButton}
            onClick={() => setShowMyRounds(true)}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={styles.cardIcon}>📊</div>
            <h3 style={styles.cardTitle}>My Rounds</h3>
            <p style={styles.cardText}>View your round history</p>
          </button>
          
         <button 
  style={styles.cardButton}
  onClick={() => setShowLeaderboard(true)}
  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
>
  <div style={styles.cardIcon}>🏆</div>
  <h3 style={styles.cardTitle}>Full Leaderboard</h3>
  <p style={styles.cardText}>Detailed season view</p>
</button>

<button 
  style={styles.cardButton}
  onClick={() => setShowFunStats(true)}
  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
>
  <div style={styles.cardIcon}>🚬</div>
  <h3 style={styles.cardTitle}>Statistics</h3>
  <p style={styles.cardText}>Performance & social stats</p>
</button>

{/* ADD PLAYERS CARD HERE */}
<button 
  style={styles.cardButton}
  onClick={() => setShowPlayers(true)}
  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
>
  <div style={styles.cardIcon}>🔥</div>
  <h3 style={styles.cardTitle}>Performance</h3>
  <p style={styles.cardText}>View players performance</p>
</button>

{pendingApprovalsCount > 0 && (
  <button 
    style={{...styles.cardButton, position: 'relative'}}
    onClick={() => setShowMarkerDashboard(true)}
    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div style={styles.cardIcon}>✅</div>
    <h3 style={styles.cardTitle}>Pending Approvals</h3>
    <p style={styles.cardText}>{pendingApprovalsCount} scorecard{pendingApprovalsCount !== 1 ? 's' : ''} to review</p>
    <div style={styles.notificationBadge}>{pendingApprovalsCount}</div>
  </button>
)}

{disputedRoundsCount > 0 && (
  <button 
    style={{...styles.cardButton, position: 'relative'}}
    onClick={() => setShowMyRounds(true)}
    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div style={styles.cardIcon}>❌</div>
    <h3 style={styles.cardTitle}>Disputed Rounds</h3>
    <p style={styles.cardText}>{disputedRoundsCount} round{disputedRoundsCount !== 1 ? 's' : ''} need{disputedRoundsCount === 1 ? 's' : ''} correction</p>
    <div style={{...styles.notificationBadge, backgroundColor: '#e53e3e'}}>{disputedRoundsCount}</div>
  </button>
)}
          {canManage && (
            <button
              style={{...styles.cardButton, position: 'relative'}}
              onClick={() => setShowAdminPanel(true)}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={styles.cardIcon}>⚙️</div>
              <h3 style={styles.cardTitle}>Admin Panel</h3>
              <p style={styles.cardText}>Manage seasons & events</p>
              {pendingMembersCount > 0 && (
                <div style={styles.notificationBadge}>{pendingMembersCount}</div>
              )}
            </button>
          )}

          {/* Season Recap card — shown to all members when a recap is published */}
          {recapAvailable && (
            <button
              style={{
                ...styles.cardButton,
                border: '1px solid #d4af37',
                boxShadow: '0 0 16px rgba(212,175,55,0.15)',
              }}
              onClick={() => setShowSeasonRecap(true)}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={styles.cardIcon}>🎬</div>
              <h3 style={styles.cardTitle}>Season Recap</h3>
              <p style={styles.cardText}>Your season in review</p>
            </button>
          )}

          {/* Site Analytics - Admin/Moderator Only */}
          {(userProfile?.role === 'admin' || userProfile?.role === 'moderator') && (
            <button
              style={styles.cardButton}
              onClick={() => setShowSiteAnalytics(true)}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={styles.cardIcon}>🚀</div>
              <h3 style={styles.cardTitle}>Site Analytics</h3>
              <p style={styles.cardText}>View usage statistics</p>
            </button>
          )}
        </div>
      </div> 
      </div>
      </div>
      

      {showScoreEntry && (
        <ScoreEntry onClose={() => {
          setShowScoreEntry(false);
          const saved = localStorage.getItem('golfRoundInProgress');
          if (!saved) {
            setShowRoundBanner(false);
            setSavedRound(null);
          }
        }} />
      )}

      {showEditScoreEntry && editRoundData && (
        <EditScoreEntry 
          onClose={() => {
            setShowEditScoreEntry(false);
            setEditRoundData(null);
          }} 
          roundData={editRoundData}
        />
      )}

      {showAdminPanel && (
  <AdminPanel 
    onClose={() => setShowAdminPanel(false)} 
    userProfile={userProfile}
    currentUserId={user.uid}
  />
)}

      {showMarkerDashboard && (
        <MarkerDashboard 
          onClose={() => setShowMarkerDashboard(false)} 
          currentUserId={user.uid}
        />
      )}

      {showMyRounds && (
        <MyRounds 
          onClose={() => setShowMyRounds(false)} 
          currentUserId={user.uid}
          onEditRound={handleEditRound}
        />
      )}

      {showLeaderboard && (
        <Leaderboard
          onClose={() => setShowLeaderboard(false)}
          onViewProfile={(userId) => {
            setShowLeaderboard(false);
            setViewingProfileId(userId);
            setShowMyProfile(true);
          }}
          hallOfFame={hallOfFame}
        />
      )}

      {showFunStats && (
        <FunStats 
          onClose={() => setShowFunStats(false)} 
          currentUserId={user.uid}
        />
      )}

     {showMyProfile && (
  <MyProfile 
    onClose={() => {
      setShowMyProfile(false);
      setViewingProfileId(null);
    }} 
    userId={viewingProfileId || user.uid}
    currentUserId={user.uid}
  />
)}

{showPlayers && (
  <Players onClose={() => setShowPlayers(false)} />
)}

{showSiteAnalytics && (
  <SiteAnalytics onClose={() => setShowSiteAnalytics(false)} />
)}

{showSeasonRecap && activeRecapSeasonId && (
  <SeasonRecap
    onClose={() => setShowSeasonRecap(false)}
    currentUserId={user.uid}
    seasonId={activeRecapSeasonId}
  />
)}

{showSettings && (
  <Settings
    onClose={() => setShowSettings(false)}
    initialTab={settingsTab}
    currentUser={user}
    userProfile={userProfile}
    onProfileUpdated={reloadUserProfile}
  />
)}

{showFinaleWinner && finaleWinnerData && (
  <FinaleWinnerPopup
    data={finaleWinnerData}
    onClose={() => {
      localStorage.setItem(`finaleWinnerDismissed_${finaleWinnerData.eventId}`, 'true');
      setShowFinaleWinner(false);
      setFinaleWinnerData(null);
    }}
  />
)}

{/* Season Recap announcement banner — shown once per season */}
{showRecapBanner && (
  <div style={{
    position: 'fixed',
    bottom: '28px',
    left: '50%',
    transform: `translateX(-50%) translateY(${bannerVisible ? '0' : '20px'})`,
    opacity: bannerVisible ? 1 : 0,
    transition: 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    backgroundColor: '#1a1a1a',
    border: '1px solid #d4af37',
    borderRadius: '16px',
    padding: '16px 20px',
    boxShadow: '0 8px 40px rgba(212,175,55,0.2), 0 4px 20px rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    zIndex: 1500,
    maxWidth: 'min(480px, calc(100vw - 32px))',
    width: '100%',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  }}>
    <div style={{
      fontSize: '36px',
      lineHeight: 1,
      flexShrink: 0,
      filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.5))',
    }}>🎬</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '14px', fontWeight: '700', color: '#ffffff', marginBottom: '2px' }}>
        Your {activeSeason?.name} Recap is here! 🎉
      </div>
      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
        Relive your season highlights, stats &amp; club awards
      </div>
    </div>
    <button
      onClick={() => { dismissRecapBanner(); setShowSeasonRecap(true); }}
      style={{
        padding: '9px 16px',
        backgroundColor: '#d4af37',
        color: '#0f0f0f',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '700',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      Watch →
    </button>
    <button
      onClick={dismissRecapBanner}
      style={{
        background: 'transparent',
        border: 'none',
        color: '#6b7280',
        cursor: 'pointer',
        fontSize: '18px',
        padding: '4px',
        lineHeight: 1,
        flexShrink: 0,
      }}
    >✕</button>
  </div>
)}

<style>{`
  @keyframes toastSlideIn { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  @keyframes toastSlideOut { from { transform: translateY(0); opacity: 1; } to { transform: translateY(-100%); opacity: 0; } }
  @keyframes shadeSlideDown { from { transform: translateY(-100%); } to { transform: translateY(0); } }
  @keyframes shadeSlideUp { from { transform: translateY(0); } to { transform: translateY(-100%); } }
`}</style>

{/* Pull handle — swipe down from top edge (or tap) opens the notification shade */}
{isMobile && !shadeOpen && (todayFinaleEvent || unreadShadeCount > 0) && (
  <div
    onClick={openShade}
    style={{
      position: 'fixed',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 8000,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 'calc(8px + env(safe-area-inset-top))',
      cursor: 'pointer',
    }}
  >
    <div style={{ width: '36px', height: '4px', backgroundColor: '#2d2d2d', borderRadius: '2px', marginBottom: '4px' }} />
    {unreadShadeCount > 0 && (
      <div style={{
        backgroundColor: '#d4af37',
        color: '#0f0f0f',
        fontSize: '10px',
        fontWeight: 700,
        borderRadius: '10px',
        padding: '2px 6px',
      }}>
        {unreadShadeCount > 9 ? '9+' : unreadShadeCount}
      </div>
    )}
  </div>
)}

{/* Toast notification popup (mobile only) */}
{isMobile && currentToast && (
  <div
    ref={toastCardRef}
    onClick={handleToastActivate}
    onTouchStart={handleToastTouchStart}
    onTouchMove={handleToastTouchMove}
    onTouchEnd={handleToastTouchEnd}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9000,
      margin: '12px',
      marginTop: 'calc(12px + env(safe-area-inset-top))',
      backgroundColor: 'rgba(30,30,30,0.97)',
      WebkitBackdropFilter: 'blur(20px)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid #2d2d2d',
      padding: '14px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      cursor: 'pointer',
      animation: toastDismissing ? 'toastSlideOut 0.3s ease forwards' : 'toastSlideIn 0.3s ease forwards',
    }}
  >
    <div style={{
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: 'rgba(212,175,55,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      flexShrink: 0,
    }}>
      {currentToast.emoji || '🔔'}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>
        {currentToast.title || 'KWAC'}
      </div>
      <div style={{
        fontSize: '12px',
        color: '#9ca3af',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        whiteSpace: 'pre-line',
      }}>
        {currentToast.type === 'bundle' ? (
          <>
            You have <span style={{ color: '#d4af37', fontWeight: 700 }}>{currentToast.count}</span> new notifications
          </>
        ) : currentToast.message}
      </div>
    </div>
    <div style={{ fontSize: '11px', color: '#6b7280', flexShrink: 0 }}>
      {formatNotifTimeAgo(currentToast.createdAtMs)}
    </div>
  </div>
)}

{/* Notification shade (mobile only) */}
{isMobile && shadeOpen && (
  <>
    <div
      onClick={closeShade}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 8500 }}
    />
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 8600,
        backgroundColor: 'rgba(15,15,15,0.97)',
        WebkitBackdropFilter: 'blur(20px)',
        backdropFilter: 'blur(20px)',
        maxHeight: '75vh',
        paddingTop: 'env(safe-area-inset-top)',
        borderRadius: '0 0 24px 24px',
        border: '1px solid #2d2d2d',
        borderTop: 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: shadeDismissing ? 'shadeSlideUp 0.35s ease forwards' : 'shadeSlideDown 0.35s ease forwards',
      }}
    >
      <div style={{
        padding: '20px 20px 12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #2d2d2d',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff' }}>🔔 Notifications</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {shadeNotifications.length > 0 && (
            shadeDeleteConfirm ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span
                  onClick={deleteAllShadeNotifications}
                  style={{
                    fontSize: '12px',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: '#ef4444',
                    padding: '4px 10px',
                    borderRadius: '6px',
                  }}
                >
                  Delete all
                </span>
                <span
                  onClick={() => setShadeDeleteConfirm(false)}
                  style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </span>
              </div>
            ) : (
              <span
                onClick={() => setShadeDeleteConfirm(true)}
                aria-label="Delete all notifications"
                style={{ fontSize: '15px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}
              >
                🗑️
              </span>
            )
          )}
          <span
            onClick={closeShade}
            style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, cursor: 'pointer' }}
          >
            Close ▲
          </span>
        </div>
      </div>
      <div style={{ width: '36px', height: '4px', backgroundColor: '#2d2d2d', borderRadius: '2px', margin: '8px auto 0', flexShrink: 0 }} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
        {shadeLoadError && (
          <div style={{
            textAlign: 'center',
            padding: '10px 14px',
            marginBottom: '8px',
            color: '#f59e0b',
            fontSize: '12px',
            background: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: '8px',
          }}>
            ⚠️ Couldn't load all notifications — check your connection and reopen.
          </div>
        )}
        {shadeNotifications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', fontSize: '14px' }}>
            🔔 No notifications yet
          </div>
        ) : (
          shadeNotifications.map((n) => (
            <div
              key={n.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '12px',
                borderRadius: '10px',
                marginBottom: '8px',
                backgroundColor: '#1e1e1e',
                border: '1px solid #2d2d2d',
                borderLeft: n.source === 'finale' ? '2px solid #d4af37' : '2px solid #10b981',
              }}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#0f0f0f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                flexShrink: 0,
              }}>
                {n.emoji || '🔔'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>{n.title}</div>
                <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.4, marginTop: '2px', whiteSpace: 'pre-line' }}>
                  {n.message}
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                {formatNotifTimeAgo(n.timeMs)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </>
)}
    </div>


  );
}

const styles = {
  container: {
  minHeight: '100vh',
  background: colors.bgDark
},
  loadingContainer: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.darkGray} 100%)`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px'
  },
  loadingText: {
    color: colors.primary,
    fontSize: '24px',
    fontWeight: 'bold'
  },
  pendingContainer: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.darkGray} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  pendingCard: {
    backgroundColor: colors.white,
    padding: '50px 40px',
    borderRadius: '16px',
    boxShadow: '0 12px 24px rgba(0,0,0,0.2)',
    maxWidth: '500px',
    textAlign: 'center',
    border: `4px solid ${colors.primary}`
  },
  pendingTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: colors.black
  },
  pendingText: {
    fontSize: '18px',
    color: colors.textGray,
    marginBottom: '15px',
    lineHeight: '1.6'
  },
  statusBarFill: {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: 'env(safe-area-inset-top)',
  background: colors.bgCard,
  zIndex: 1000,
  pointerEvents: 'none'
},
  header: {
  background: colors.bgCard,
  color: colors.white,
  paddingLeft: '24px',
  paddingRight: '24px',
  paddingTop: 'calc(16px + env(safe-area-inset-top))',
  paddingBottom: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  borderBottom: `1px solid ${colors.borderSubtle}`,
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 999,
  transition: 'transform 0.3s ease'
},
  headerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  logo: {
  height: '60px',
  width: 'auto'
},
  headerTitle: {
    margin: 0,
    fontSize: '32px',
    fontWeight: 'bold',
    color: colors.primary,
    letterSpacing: '2px'
  },
  bannerContainer: {
    padding: '20px',
    background: 'transparent'
  },
  banner: {
  background: colors.bgCard,
  color: colors.textPrimary,
  padding: '24px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  cursor: 'pointer',
  boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  transition: 'all 0.3s ease',
  border: `1px solid ${colors.gold}`
},
  bannerIcon: {
    fontSize: '40px',
    flexShrink: 0
  },
  bannerContent: {
    flex: 1,
    minWidth: 0
  },
  bannerTitle: {
  fontSize: '18px',
  fontWeight: '600',
  marginBottom: '4px',
  color: colors.gold,
  letterSpacing: '-0.01em'
},
bannerSubtitle: {
  fontSize: '14px',
  color: colors.textSecondary,
  fontWeight: '400'
},
  bannerAction: {
    flexShrink: 0
  },
  bannerButton: {
  fontSize: '14px',
  fontWeight: '600',
  padding: '10px 20px',
  backgroundColor: colors.gold,
  color: colors.bgDark,
  borderRadius: '8px',
  display: 'inline-block'
},
  beerButton: {
    padding: '10px 14px',
    fontSize: '16px',
    fontWeight: '700',
    backgroundColor: colors.gold,
    color: colors.bgDark,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    minWidth: '44px',
    transition: 'all 0.2s ease'
  },
  bannerClose: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    backgroundColor: colors.black,
    border: 'none',
    color: colors.primary,
    fontSize: '20px',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '6px',
    fontWeight: 'bold',
    transition: 'all 0.2s'
  },
  userInfo: {
  display: 'flex',
  alignItems: 'center',
  gap: '15px'
},
profileButton: {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: '50%'
},
profilePhoto: {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #d4af37',
  flexShrink: 0
},
profilePhotoPlaceholder: {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: '#d4af37',
  border: '2px solid #2d2d2d',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px',
  fontWeight: '700',
  color: '#0f0f0f',
  flexShrink: 0
},
userDetails: {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '2px'
},
userName: {
  fontSize: '15px',
  fontWeight: '600',
  color: colors.textPrimary,
  letterSpacing: '-0.01em'
},
profileChevron: {
  color: '#9ca3af',
  flexShrink: 0
},
  officerBadge: {
    backgroundColor: colors.primary,
    color: colors.black,
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  logoutButton: {
    padding: '10px 20px',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  logoutButtonAlt: {
    padding: '12px 30px',
    backgroundColor: colors.black,
    color: colors.primary,
    border: `2px solid ${colors.primary}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px',
    marginTop: '20px',
    transition: 'all 0.2s'
  },
  content: {
  padding: '32px 24px',
  maxWidth: '1400px',
  margin: '0 auto'
},
  welcomeSection: {
  marginBottom: '24px'
},
 dashboardTitle: {
  fontSize: '32px',
  fontWeight: '700',
  color: colors.textPrimary,
  margin: '0 0 8px 0',
  letterSpacing: '-0.02em'
},
welcomeText: {
  fontSize: '16px',
  fontWeight: '400',
  color: colors.textSecondary,
  margin: 0,
  letterSpacing: '-0.01em'
},
statsGrid: {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '12px',
  marginBottom: '20px'
},
statCard: {
  background: colors.bgCard,
  border: `1px solid ${colors.borderSubtle}`,
  borderRadius: '8px',
  padding: '10px 8px',
  transition: 'all 0.3s ease',
  cursor: 'default'
},
statCardHeader: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '8px'
},
statCardLabel: {
  fontSize: '10px',
  fontWeight: '600',
  color: colors.textTertiary,
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
},
statCardIcon: {
  fontSize: '20px',
  opacity: 0.5
},
statCardValue: {
  fontSize: '22px',
  fontWeight: '700',
  color: colors.gold,
  marginBottom: '2px',
  letterSpacing: '-0.03em',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: '1'
},
statCardFooter: {
  fontSize: '12px',
  color: colors.textTertiary,
  fontWeight: '400'
},
  grid: {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: '16px'
},
 cardButton: {
  backgroundColor: colors.bgCard,
  padding: '24px 20px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  textAlign: 'center',
  border: `1px solid ${colors.borderSubtle}`,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  fontSize: '16px',
},
  cardIcon: {
  fontSize: '32px',
  marginBottom: '8px',
  display: 'block'
},
 cardTitle: {
  fontSize: '15px',
  fontWeight: '600',
  color: colors.gold,
  margin: '0 0 6px 0',
  letterSpacing: '-0.01em'
},
cardText: {
  fontSize: '13px',
  color: colors.textSecondary,
  margin: 0,
  fontWeight: '400'
},
  notificationBadge: {
    position: 'absolute',
    top: '-12px',
    right: '-12px',
    backgroundColor: colors.error,
    color: colors.white,
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    border: `3px solid ${colors.white}`
  },
  
  // Stats Container (side by side)
  mainLayout: {
  display: 'grid',
  gridTemplateColumns: '400px 1fr',
  gap: '20px',
  marginBottom: '20px'
},
leftColumn: {
  display: 'flex',
  flexDirection: 'column',
  gap: '15px'
},
rightColumn: {
  flex: 1
},
leaderboardWidget: {
  backgroundColor: colors.bgCard,
  border: `1px solid ${colors.borderSubtle}`,
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'none'
},
leaderboardHeader: {
  background: colors.bgCard,
  padding: '20px',
  borderBottom: `1px solid ${colors.borderSubtle}`
},
leaderboardHeaderTitle: {
  fontSize: '18px',
  fontWeight: '600',
  color: colors.gold,
  margin: '0 0 6px 0',
  letterSpacing: '-0.01em'
},
leaderboardSubtitle: {
  fontSize: '13px',
  color: colors.textSecondary,
  margin: 0,
  fontWeight: '400'
},
leaderboardScrollable: {
  flex: 1,
  overflowY: 'auto',
  backgroundColor: colors.bgCard,
  padding: '15px'
},
hofWidget: {
  backgroundColor: colors.bgCard,
  border: `1px solid ${colors.borderSubtle}`,
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
},
  statsToggleGold: {
  width: '100%',
  padding: '18px 20px',
  background: colors.bgCard,
  border: 'none',
  borderBottom: `1px solid ${colors.borderSubtle}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease'
},
  toggleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  toggleIcon: {
    fontSize: '24px'
  },
  toggleText: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: colors.primary
  },
  toggleArrow: {
    fontSize: '16px',
    color: colors.primary,
    fontWeight: 'bold'
  },
  toggleTextGold: {
  fontSize: '16px',
  fontWeight: '600',
  color: colors.gold,
  letterSpacing: '-0.01em'
},
toggleArrowGold: {
  fontSize: '16px',
  color: colors.gold,
  fontWeight: '600'
},
  statsContent: {
  backgroundColor: colors.bgCard,
  padding: '15px'
},
  statsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  statsRow: {
  display: 'grid',
  gridTemplateColumns: '40px 1fr 55px 50px',
  gap: '12px',
  padding: '12px 16px',
  backgroundColor: colors.bgDark,
  borderRadius: '8px',
  alignItems: 'center',
  overflow: 'hidden',
  marginBottom: '6px',
  transition: 'all 0.2s ease'
},
statsPosition: {
  fontSize: '14px',
  fontWeight: '600',
  color: colors.textSecondary,
  textAlign: 'center'
},
statsPlayer: {
  fontSize: '14px',
  fontWeight: '500',
  color: colors.textPrimary,
  wordBreak: 'break-word',
  minWidth: 0,
  overflow: 'hidden'
},
statsValue: {
  fontSize: '15px',
  fontWeight: '600',
  color: colors.gold,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums'
},
  statsLoading: {
  padding: '40px',
  textAlign: 'center',
  color: colors.textSecondary,
  fontSize: '14px',
  fontWeight: '400'
},
statsEmpty: {
  padding: '40px',
  textAlign: 'center',
  color: colors.textTertiary,
  fontSize: '14px',
  fontStyle: 'italic',
  fontWeight: '400'
},
  statsDetailsButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: colors.primary,
    color: colors.black,
    border: 'none',
    fontSize: '13px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '10px',
    borderRadius: '6px'
  },
  // Hall of Fame compact styles
  championRow: {
  display: 'flex',
  gap: '12px',
  padding: '12px',
  backgroundColor: colors.bgDark,
  border: `1px solid ${colors.borderSubtle}`,
  borderRadius: '8px',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  marginBottom: '6px'
},
  championPhotoSmall: {
    flexShrink: 0
  },
  championPhotoImgSmall: {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: `2px solid ${colors.borderSubtle}`
},
  championPhotoPlaceholderSmall: {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: colors.gold,
  border: `2px solid ${colors.borderSubtle}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '16px',
  fontWeight: '600',
  color: colors.bgDark
},
  championInfoSmall: {
    flex: 1,
    minWidth: 0
  },
  championNameSmall: {
  fontSize: '14px',
  fontWeight: '600',
  color: colors.gold,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
},
championYearSmall: {
  fontSize: '12px',
  color: colors.textSecondary,
  fontWeight: '500'
},
  recordsWidget: {
  backgroundColor: colors.bgCard,
  border: `1px solid ${colors.borderSubtle}`,
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
},
recordRow: {
  display: 'flex',
  gap: '12px',
  padding: '12px',
  backgroundColor: colors.bgDark,
  border: `1px solid ${colors.borderSubtle}`,
  borderRadius: '8px',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  marginBottom: '6px'
},
recordIcon: {
  fontSize: '24px',
  flexShrink: 0
},
recordInfo: {
  flex: 1,
  minWidth: 0
},
recordLabel: {
  fontSize: '11px',
  color: colors.textTertiary,
  textTransform: 'uppercase',
  fontWeight: '600',
  letterSpacing: '0.05em'
},
recordPlayer: {
  fontSize: '14px',
  fontWeight: '600',
  color: colors.gold,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
},
recordValue: {
  fontSize: '14px',
  fontWeight: '600',
  color: colors.gold,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums'
},
};

export default App;